import React, { useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { FluentProvider, teamsLightTheme } from "@fluentui/react-components";
import {
    ProductCard,
    ProductData,
} from "../../components/chatWidget/components/ProductCard/ProductCard";

type ProductsPayload = { type: "products"; data: ProductData[] } | ProductData[];

const MAX_DATA_PARAM_LEN = 4000;
const MAX_DECODED_BYTES = 20_000;
const MAX_PRODUCTS = 2;

function isHttpUrl(value: unknown): value is string {
    if (typeof value !== "string") return false;
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

function clampStr(value: unknown, maxLen: number): string {
    if (typeof value !== "string") return "";
    return value.length > maxLen ? value.slice(0, maxLen) : value;
}

function sanitizePrice(value: unknown): number | undefined {
    if (typeof value !== "number") return undefined;
    if (!Number.isFinite(value)) return undefined;
    return value;
}

function sanitizeProduct(raw: any): ProductData | null {
    const name = clampStr(raw?.name, 120);
    if (!name) return null;

    const url = isHttpUrl(raw?.url) ? raw.url : undefined;
    const image_url = isHttpUrl(raw?.image_url) ? raw.image_url : undefined;

    const price = sanitizePrice(raw?.price);
    const currency = clampStr(raw?.currency, 10) || undefined;

    const button_label = clampStr(raw?.button_label, 40) || undefined;

    return {
        name,
        url,
        image_url,
        price,
        currency,
        button_label,
    } as ProductData;
}

function decodeBase64UrlToJson<T>(b64url: string): T {
    if (!b64url || b64url.length > MAX_DATA_PARAM_LEN) {
        throw new Error("data param too large or empty");
    }

    const base64 =
        b64url.replace(/-/g, "+").replace(/_/g, "/") +
        "=".repeat((4 - (b64url.length % 4)) % 4);

    const binary = window.atob(base64);

    if (binary.length > MAX_DECODED_BYTES) {
        throw new Error("decoded payload too large");
    }

    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    const jsonString = new TextDecoder("utf-8", { fatal: false }).decode(bytes);

    return JSON.parse(jsonString) as T;
}

function normalizeProductsPayload(parsed: ProductsPayload): ProductData[] {
    const items = Array.isArray(parsed)
        ? parsed
        : parsed && (parsed as any).type === "products" && Array.isArray((parsed as any).data)
            ? (parsed as any).data
            : [];

    if (!Array.isArray(items) || items.length === 0) return [];

    return items
        .slice(0, MAX_PRODUCTS)
        .map(sanitizeProduct)
        .filter(Boolean) as ProductData[];
}

export const TelegramProductsPage = () => {
    const [searchParams] = useSearchParams();

    const products: ProductData[] = useMemo(() => {
        const dataParam = searchParams.get("data");
        if (!dataParam) return [];

        try {
            const parsed = decodeBase64UrlToJson<ProductsPayload>(dataParam);
            return normalizeProductsPayload(parsed);
        } catch (e) {
            console.error("Failed to parse/sanitize products payload:", e);
            return [];
        }
    }, [searchParams]);

    useEffect(() => {
        const tg = (window as any).Telegram?.WebApp;
        if (tg) {
            tg.ready();
            tg.expand();
        }
    }, []);

    return (
        <FluentProvider theme={teamsLightTheme}>
            <div
                style={{
                    padding: 16,
                    backgroundColor: "#f3f4f6",
                    minHeight: "100vh",
                    boxSizing: "border-box",
                }}
            >
                {products.length === 0 ? (
                    <div
                        style={{
                            textAlign: "center",
                            marginTop: 40,
                            color: "#605e5c",
                            fontFamily: "Segoe UI",
                        }}
                    >
                        <p>No products found.</p>
                    </div>
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                        {products.map((product) => {
                            const key =
                                product.url ||
                                `${product.name}-${product.price ?? "na"}-${product.currency ?? "na"}`;
                            return (
                                <div key={key} style={{ maxWidth: "100%" }}>
                                    <ProductCard product={product} />
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </FluentProvider>
    );
};

export default TelegramProductsPage;
